import { Input, Modal, Select, Segmented, Switch, message } from 'antd';
import { motion } from 'framer-motion';
import { useState } from 'react';
import {
  type WorkLibraryItem,
  type WorkLibraryKind,
  type WorkTypeKey,
} from '../../../../../services/epcWorkLibraryService';
import { useCreateLibraryItem, useUpdateLibraryItem } from '../../hooks/useWorkLibrary';
import { SOVEREIGN } from '../../../utils/epcProjectVisual';
import { slugify } from '../../utils/disciplineKey';
import { WORK_TYPE_LABELS } from './gates/gateLibrary';

type Mode = 'create' | 'edit';

interface Props {
  open: boolean;
  mode: Mode;
  kind: WorkLibraryKind;
  disciplineKey: string | null;
  disciplineLabel: string | null;
  /** Prefill when editing, or the typed text when creating. */
  initial?: Partial<Pick<WorkLibraryItem, 'id' | 'label' | 'workInstruction' | 'workTypeKey'>>;
  /** Admin master-data curation: always save, and let the admin type a discipline. */
  adminMode?: boolean;
  onClose: () => void;
  /** Returns the resulting item (persisted or synthetic for use-once). */
  onResolved: (item: WorkLibraryItem) => void;
}

export function LibraryItemEditorPopover({
  open,
  mode,
  kind,
  disciplineKey,
  disciplineLabel,
  initial,
  adminMode = false,
  onClose,
  onResolved,
}: Props) {
  const [label, setLabel] = useState(initial?.label ?? '');
  const [instruction, setInstruction] = useState(initial?.workInstruction ?? '');
  const [save, setSave] = useState(true); // create mode: Save to library vs Use once
  const [common, setCommon] = useState(disciplineKey === null);
  const [disciplineInput, setDisciplineInput] = useState(''); // admin create: typed discipline ('' = Common)
  const isGateKind = kind === 'education' || kind === 'safety' || kind === 'qaqc';
  const [workType, setWorkType] = useState<WorkTypeKey | null>(initial?.workTypeKey ?? null);

  const createMut = useCreateLibraryItem();
  const updateMut = useUpdateLibraryItem();

  // Re-sync local state whenever the popover (re)opens with new initial values.
  function afterOpenChange(isOpen: boolean) {
    if (isOpen) {
      setLabel(initial?.label ?? '');
      setInstruction(initial?.workInstruction ?? '');
      setSave(true);
      setCommon(disciplineKey === null);
      setDisciplineInput('');
      setWorkType(initial?.workTypeKey ?? null);
    }
  }

  async function handleOk() {
    const trimmed = label.trim();
    if (!trimmed) {
      message.error('Label is required');
      return;
    }
    // Admin create derives discipline from the free-text input (blank = Common);
    // otherwise honour the Common switch against the contextual discipline.
    const adminCreate = adminMode && mode === 'create';
    const targetKey = adminCreate
      ? disciplineInput.trim()
        ? slugify(disciplineInput)
        : null
      : common
        ? null
        : disciplineKey;
    const targetLabel = adminCreate
      ? disciplineInput.trim()
        ? disciplineInput.trim()
        : null
      : common
        ? null
        : disciplineLabel;
    // Admin master-data curation always persists; there is no "use once".
    const willSave = adminCreate || save;

    try {
      if (mode === 'edit' && initial?.id) {
        const item = await updateMut.mutateAsync({
          id: initial.id,
          input: { label: trimmed, workInstruction: instruction.trim(), ...(adminMode && isGateKind ? { workTypeKey: workType } : {}) },
        });
        message.success('Library item updated');
        onResolved(item);
      } else if (willSave) {
        const item = await createMut.mutateAsync({
          kind,
          label: trimmed,
          workInstruction: instruction.trim(),
          disciplineKey: targetKey,
          disciplineLabel: targetLabel,
          // Master-page curation is org-wide; the backend downgrades to private
          // when the caller lacks governance. DM saves omit visibility so the
          // private default keeps them proposal-scoped.
          ...(adminCreate ? { visibility: 'org' as const } : {}),
          ...(adminMode && isGateKind ? { workTypeKey: workType } : {}),
        });
        message.success('Added to library');
        onResolved(item);
      } else {
        // Use once: synthetic, not persisted.
        onResolved({
          id: `local-${trimmed}`,
          kind,
          label: trimmed,
          workInstruction: instruction.trim(),
          disciplineKey: targetKey,
          disciplineLabel: targetLabel,
          active: true,
          visibility: 'private',
        });
      }
      onClose();
    } catch (e) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Could not save the item';
      message.error(msg);
    }
  }

  return (
    <Modal
      open={open}
      afterOpenChange={afterOpenChange}
      onCancel={onClose}
      onOk={() => void handleOk()}
      okText={
        mode === 'edit'
          ? 'Save changes'
          : adminMode || save
            ? 'Save to library'
            : 'Use once'
      }
      confirmLoading={createMut.isPending || updateMut.isPending}
      title={mode === 'edit' ? 'Edit library item' : 'Add to library'}
      destroyOnClose
      transitionName="" // disable antd zoom; use our own subtle scale
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
        className="space-y-3 pt-1"
      >
        <div>
          <div className="mb-1 text-[11px] font-semibold" style={{ color: SOVEREIGN.kpiCaption }}>
            Label
          </div>
          <Input
            maxLength={200}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={kind === 'safety' ? 'e.g. Excavation permit' : 'e.g. Pit dimension check'}
          />
        </div>
        <div>
          <div className="mb-1 text-[11px] font-semibold" style={{ color: SOVEREIGN.kpiCaption }}>
            Work instruction (auto-loads when picked)
          </div>
          <Input.TextArea
            rows={3}
            maxLength={2000}
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder="Method / acceptance / sequence…"
          />
        </div>
        {mode === 'create' && adminMode && (
          <div>
            <div
              className="mb-1 text-[11px] font-semibold"
              style={{ color: SOVEREIGN.kpiCaption }}
            >
              Discipline
            </div>
            <Input
              maxLength={120}
              value={disciplineInput}
              onChange={(e) => setDisciplineInput(e.target.value)}
              placeholder="Discipline (e.g. Civil) — leave blank for Common"
            />
            <div className="mt-1 text-[11px]" style={{ color: SOVEREIGN.kpiCaption }}>
              Leave blank to make this item Common (available to all disciplines).
            </div>
          </div>
        )}
        {adminMode && isGateKind && (
          <div>
            <div className="mb-1 text-[11px] font-semibold" style={{ color: SOVEREIGN.kpiCaption }}>
              Work type (gate pack)
            </div>
            <Select
              allowClear
              className="w-full"
              placeholder="None — not part of a pack"
              value={workType}
              onChange={(v) => setWorkType((v as WorkTypeKey | undefined) ?? null)}
              options={Object.entries(WORK_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <div className="mt-1 text-[11px]" style={{ color: SOVEREIGN.kpiCaption }}>
              Items sharing a work type form a one-click Gate Pack (needs at least one
              Education + one Safety item on the same work type).
            </div>
          </div>
        )}
        {mode === 'create' && !adminMode && (
          <>
            <Segmented<'save' | 'once'>
              block
              value={save ? 'save' : 'once'}
              onChange={(v) => setSave(v === 'save')}
              options={[
                { value: 'save', label: 'Save to library' },
                { value: 'once', label: 'Use once' },
              ]}
            />
            {save && disciplineKey !== null && (
              <label className="flex items-center gap-2 text-[12px]" style={{ color: SOVEREIGN.ink }}>
                <Switch size="small" checked={common} onChange={setCommon} />
                Available to all disciplines (Common)
              </label>
            )}
          </>
        )}
      </motion.div>
    </Modal>
  );
}
